// Schema Registry - Based on Avro schemas
export const FIELD_SCHEMA = {
  core: {
    label: '📌 Core',
    fields: {
      'type': { type: 'string', description: 'Event type', values: ['track', 'identify', 'page', 'screen', 'group', 'alias'] },
      'event': { type: 'string', description: 'Event name' },
      'timestamp': { type: 'timestamp', description: 'Event timestamp' },
      'entity_gid': { type: 'uuid', description: 'Entity global ID' },
      'message_id': { type: 'string', description: 'Message identifier' },
      'event_gid': { type: 'uuid', description: 'Event global ID' },
    }
  },
  identity: {
    label: '👤 Identity',
    fields: {
      'user_id': { type: 'string', description: 'User identifier' },
      'user_gid': { type: 'uuid', description: 'User global ID' },
      'anonymous_id': { type: 'string', description: 'Anonymous identifier' },
      'anonymous_gid': { type: 'uuid', description: 'Anonymous global ID' },
      'session_id': { type: 'string', description: 'Session identifier' },
      'session_gid': { type: 'uuid', description: 'Session global ID' },
      'account_id': { type: 'string', description: 'Account identifier' },
      'previous_id': { type: 'string', description: 'Previous identifier (for alias)' },
    }
  },
  commerce: {
    label: '💰 Commerce',
    fields: {
      'commerce.order_id': { type: 'string', description: 'Order ID' },
      'commerce.cart_id': { type: 'string', description: 'Shopping cart ID' },
      'commerce.checkout_id': { type: 'string', description: 'Checkout ID' },
      'commerce.total': { type: 'float', description: 'Total amount' },
      'commerce.revenue': { type: 'float', description: 'Revenue amount' },
      'commerce.subtotal': { type: 'float', description: 'Subtotal before tax/shipping' },
      'commerce.tax': { type: 'float', description: 'Tax amount' },
      'commerce.shipping': { type: 'float', description: 'Shipping cost' },
      'commerce.discount': { type: 'float', description: 'Discount amount' },
      'commerce.currency': { type: 'string', description: 'Currency code', pattern: '[A-Z]{3}' },
      'commerce.coupon': { type: 'string', description: 'Coupon code' },
      'commerce.affiliation': { type: 'string', description: 'Affiliation name' },
    }
  },
  products: {
    label: '📦 Products',
    fields: {
      'products[].name': { type: 'string', description: 'Product name' },
      'products[].product_id': { type: 'string', description: 'Product ID' },
      'products[].sku': { type: 'string', description: 'Stock keeping unit' },
      'products[].price': { type: 'float', description: 'Product price' },
      'products[].quantity': { type: 'float', description: 'Quantity' },
      'products[].category': { type: 'string', description: 'Product category' },
      'products[].brand': { type: 'string', description: 'Brand name' },
      'products[].variant': { type: 'string', description: 'Product variant' },
      'products[].discount': { type: 'float', description: 'Discount on product' },
      'products[].revenue': { type: 'float', description: 'Revenue from product' },
    }
  },
  enrichment: {
    label: '🧠 AI Enrichment',
    fields: {
      'sentiment[].sentiment': { type: 'string', description: 'Sentiment value', values: ['positive', 'negative', 'neutral', 'mixed'] },
      'sentiment[].reason': { type: 'string', description: 'Reasoning for sentiment' },
      'classification[].value': { type: 'string', description: 'Classification label' },
      'classification[].type': { type: 'string', description: 'Classification type' },
      'classification[].confidence': { type: 'float', description: 'Confidence score', min: 0, max: 1 },
      'classification[].score': { type: 'float', description: 'Classification score', min: 0, max: 1 },
      'analysis[].topic': { type: 'string', description: 'Analysis topic' },
    }
  },
  context: {
    label: '📍 Context',
    fields: {
      'location[].country': { type: 'string', description: 'Country name' },
      'location[].country_code': { type: 'string', description: 'Country code' },
      'location[].region': { type: 'string', description: 'Region/state' },
      'location[].locality': { type: 'string', description: 'City/locality' },
      'location[].postal_code': { type: 'string', description: 'Postal/ZIP code' },
      'location[].latitude': { type: 'float', description: 'Latitude', min: -90, max: 90 },
      'location[].longitude': { type: 'float', description: 'Longitude', min: -180, max: 180 },
      'device.type': { type: 'string', description: 'Device type', values: ['mobile', 'desktop', 'tablet', 'tv', 'wearable'] },
      'device.manufacturer': { type: 'string', description: 'Device manufacturer' },
      'device.model': { type: 'string', description: 'Device model' },
      'os.name': { type: 'string', description: 'Operating system name' },
      'os.version': { type: 'string', description: 'OS version' },
      'page.url': { type: 'string', description: 'Page URL' },
      'page.title': { type: 'string', description: 'Page title' },
      'page.path': { type: 'string', description: 'Page path' },
      'page.referrer': { type: 'string', description: 'Referrer URL' },
      'screen.name': { type: 'string', description: 'Screen name (mobile)' },
      'campaign.name': { type: 'string', description: 'Campaign name' },
      'campaign.source': { type: 'string', description: 'Campaign source' },
      'campaign.medium': { type: 'string', description: 'Campaign medium' },
      'context_ip': { type: 'string', description: 'IP address' },
    }
  },
  custom: {
    label: '📊 Custom Data',
    fields: {
      'properties': { type: 'map', description: 'Custom properties (string values)' },
      'metrics': { type: 'map', description: 'Custom metrics (numeric values)' },
      'dimensions': { type: 'map', description: 'Custom dimensions (string values)' },
      'flags': { type: 'map', description: 'Feature flags (boolean values)' },
      'traits': { type: 'object', description: 'User traits' },
      'content': { type: 'map', description: 'Content data' },
    }
  },
  involvement: {
    label: '👥 Participants',
    fields: {
      'involves[].label': { type: 'string', description: 'Participant label' },
      'involves[].role': { type: 'string', description: 'Participant role' },
      'involves[].entity_type': { type: 'string', description: 'Entity type' },
      'involves[].id': { type: 'string', description: 'Participant ID' },
    }
  },
  metadata: {
    label: '🔧 Metadata',
    fields: {
      'partition': { type: 'string', description: 'Data partition' },
      'write_key': { type: 'string', description: 'Write key used' },
      'importance': { type: 'int', description: 'Event importance level' },
      'customer_facing': { type: 'boolean', description: 'Customer-facing event' },
      'ttl_days': { type: 'float', description: 'Time to live in days' },
      'received_at': { type: 'timestamp', description: 'Server receive timestamp' },
      'sent_at': { type: 'timestamp', description: 'Client send timestamp' },
      'original_timestamp': { type: 'timestamp', description: 'Original timestamp' },
      'local_time': { type: 'timestamp', description: 'Local time' },
    }
  }
};

// Flatten schema for easy lookup
export const FLAT_FIELD_SCHEMA = Object.values(FIELD_SCHEMA).reduce((acc, category) => {
  return { ...acc, ...category.fields };
}, {});

// Operators by field type
export const OPERATORS = {
  string: [
    { value: 'equals', label: '= equals', jsonpath: '== "{value}"' },
    { value: 'contains', label: '⊃ contains', jsonpath: '=~ /.*{value}.*/i' },
    { value: 'startsWith', label: '← starts with', jsonpath: '=~ /^{value}.*/i' },
    { value: 'endsWith', label: '→ ends with', jsonpath: '=~ /.*{value}$/i' },
    { value: 'notEquals', label: '≠ not equals', jsonpath: '!= "{value}"' },
    { value: 'exists', label: '? exists', jsonpath: '', noValue: true },
  ],
  float: [
    { value: 'equals', label: '= equals', jsonpath: '== {value}' },
    { value: 'gt', label: '> greater than', jsonpath: '> {value}' },
    { value: 'lt', label: '< less than', jsonpath: '< {value}' },
    { value: 'gte', label: '≥ greater or equal', jsonpath: '>= {value}' },
    { value: 'lte', label: '≤ less or equal', jsonpath: '<= {value}' },
    { value: 'between', label: '⇄ between', jsonpath: '>= {min} && <= {max}', range: true },
    { value: 'exists', label: '? exists', jsonpath: '', noValue: true },
  ],
  int: [
    { value: 'equals', label: '= equals', jsonpath: '== {value}' },
    { value: 'gt', label: '> greater than', jsonpath: '> {value}' },
    { value: 'lt', label: '< less than', jsonpath: '< {value}' },
    { value: 'gte', label: '≥ greater or equal', jsonpath: '>= {value}' },
    { value: 'lte', label: '≤ less or equal', jsonpath: '<= {value}' },
    { value: 'exists', label: '? exists', jsonpath: '', noValue: true },
  ],
  boolean: [
    { value: 'isTrue', label: '✓ is true', jsonpath: '== true', noValue: true },
    { value: 'isFalse', label: '✗ is false', jsonpath: '== false', noValue: true },
    { value: 'exists', label: '? exists', jsonpath: '', noValue: true },
  ],
  timestamp: [
    { value: 'after', label: '> after', jsonpath: '> {value}' },
    { value: 'before', label: '< before', jsonpath: '< {value}' },
    { value: 'between', label: '⇄ between', jsonpath: '>= {min} && <= {max}', range: true },
    { value: 'exists', label: '? exists', jsonpath: '', noValue: true },
  ],
  uuid: [
    { value: 'equals', label: '= equals', jsonpath: '== "{value}"' },
    { value: 'exists', label: '? exists', jsonpath: '', noValue: true },
  ],
  map: [
    { value: 'hasKey', label: '🔑 has key', jsonpath: '.{key}', mapKey: true },
    { value: 'keyEquals', label: '🔑= key equals value', jsonpath: '.{key} == "{value}"', mapKeyValue: true },
  ],
  object: [
    { value: 'exists', label: '? exists', jsonpath: '', noValue: true },
  ]
};

// Structure icons for different field types
export const STRUCTURE_ICON_MAP = {
  properties: '🔧',
  dimensions: '📐',
  metrics: '📊',
  flags: '🚩',
  traits: '🧬',
  content: '📝',
  sentiment: '🧠',
  classification: '🏷️',
  involves: '👥',
  location: '📍',
  analysis: '🧪',
  commerce: '🛒',
  products: '📦',
  productline: '📦',
  device: '📱',
  os: '🖥️',
  app: '📱',
  page: '🧭',
  screen: '🖥️',
  user_agent: '🧾',
  useragent: '🧾',
  campaign: '🎯',
  integrations: '🔌',
  accesses: '🔐',
  base_events: '🗂️',
};

// Date field names that should be formatted as timestamps
export const DATE_FIELDS = [
  'timestamp',
  'original_timestamp',
  'received_at',
  'sent_at',
  'local_time',
  'ingestedAt',
  'ingested_at',
  'last_event_at',
];

export const DATE_FIELD_SET = new Set(DATE_FIELDS.map((field) => field.toLowerCase()));

// Get operators for a field
export function getOperatorsForField(fieldPath) {
  const schema = FLAT_FIELD_SCHEMA[fieldPath];
  if (!schema) return OPERATORS.string; // default to string operators

  const type = schema.type;
  return OPERATORS[type] || OPERATORS.string;
}